<div class="flex min-h-0 flex-1 flex-col gap-4 pt-5">
  <!-- Toolbar -->
  <div class="flex items-center gap-2.5 px-6">
    <div class="flex h-11 flex-1 items-center gap-2 rounded-control bg-card px-3.5 shadow-hairline focus-within:ring-2 focus-within:ring-focus">
      <app-icon name="icon-search" [size]="18" class="flex-none text-ink-3" />
      <input
        type="text"
        [ngModel]="query"
        (ngModelChange)="onQueryInput($event)"
        placeholder="Search device ID or serial number — across all tenants"
        class="w-full border-none bg-transparent p-0 text-[13px] leading-5 text-ink-1 outline-none placeholder:text-ink-3"
      />
    </div>

    <div class="relative">
      <button
        type="button"
        (click)="toggleTenantMenu()"
        class="flex h-11 items-center gap-1.5 rounded-control px-3.5 text-[12.5px] font-medium shadow-hairline"
        [class.bg-surface-2]="isTenantFilterNarrowed()"
        [class.text-ink-1]="isTenantFilterNarrowed()"
        [class.bg-card]="!isTenantFilterNarrowed()"
        [class.text-ink-2]="!isTenantFilterNarrowed()"
      >
        Tenant{{ isTenantFilterNarrowed() ? ' (' + tenantFilter().size + ')' : '' }}
        <app-icon [name]="tenantMenuOpen() ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'" [size]="18" />
      </button>
      @if (tenantMenuOpen()) {
        <div class="absolute right-0 top-[calc(100%+6px)] z-20 max-h-64 w-56 overflow-y-auto rounded-control bg-card p-1 shadow-raised">
          @for (t of allTenants(); track t.id) {
            <label class="flex cursor-pointer items-center gap-2 rounded-[4px] px-2.5 py-2 text-[13px] text-ink-1 hover:bg-sunken">
              <input type="checkbox" [checked]="tenantFilter().has(t.id)" (change)="toggleTenantFilter(t.id)" />
              {{ t.name }}
            </label>
          } @empty {
            <div class="px-2.5 py-2 text-[12px] text-ink-3">No tenants</div>
          }
        </div>
      }
    </div>

    <div class="flex h-11 items-center rounded-control bg-card px-3.5 shadow-hairline focus-within:ring-2 focus-within:ring-focus">
      <input
        type="text"
        [ngModel]="currentVersion"
        (ngModelChange)="onVersionInput($event)"
        placeholder="Current version"
        class="w-28 border-none bg-transparent p-0 text-[12.5px] leading-5 text-ink-1 outline-none placeholder:text-ink-3"
      />
    </div>

    <button
      type="button"
      (click)="toggleHideAssigned()"
      class="flex h-11 items-center gap-1.5 rounded-control px-3.5 text-[12.5px] font-medium shadow-hairline"
      [class.bg-surface-2]="hideAssigned()"
      [class.text-ink-1]="hideAssigned()"
      [class.bg-card]="!hideAssigned()"
      [class.text-ink-2]="!hideAssigned()"
    >
      <span
        class="flex h-4 w-4 flex-none items-center justify-center rounded-[4px]"
        [class.bg-action]="hideAssigned()"
        [class.border]="!hideAssigned()"
        [class.border-hairline]="!hideAssigned()"
      >
        @if (hideAssigned()) {
          <app-icon name="icon-checkmark" [size]="11" class="text-action-text" />
        }
      </span>
      Hide already assigned
    </button>
  </div>

  <!-- Bulk-action row -->
  <div class="flex items-center gap-4 px-6 text-[11.5px] font-medium text-link">
    <button type="button" (click)="expandAll()" class="hover:underline">Expand all</button>
    <button type="button" (click)="collapseAll()" class="hover:underline">Collapse all</button>
    <button type="button" (click)="selectAllEligible()" [disabled]="isSelectingAll()" class="hover:underline disabled:opacity-50">
      Select all eligible ({{ data()?.totalMatchingFilter ?? 0 }})
    </button>
    <span class="ml-auto font-normal text-ink-3">
      {{ data()?.totalMatchingFilter ?? 0 }} devices not yet assigned this update
    </span>
  </div>

  <!-- Grouped table. Extra bottom padding so the last rows can scroll clear
       of the floating selection pill, which overlays content rather than
       reserving its own layout space. -->
  <div class="min-h-0 flex-1 overflow-auto px-6 pb-24">
    <div class="min-h-full overflow-hidden rounded-card shadow-hairline">
      @if (isLoading() && !data()) {
        <!-- Skeleton on the grouped-list geometry: tenant group header rows
             with device rows beneath, so the real groups land in place. -->
        <div class="grid-skeleton" aria-hidden="true">
          @for (group of [0, 1]; track group) {
            <div class="flex items-center gap-3 border-b border-hairline bg-sunken px-4 py-3">
              <div class="h-[16px] w-[16px] flex-none animate-pulse rounded-[4px] bg-surface-2"></div>
              <div class="h-4 w-40 animate-pulse rounded bg-surface-2"></div>
            </div>
            @for (bone of [0, 1, 2]; track bone) {
              <div class="flex items-center gap-3 border-b border-hairline px-4 py-3.5">
                <div class="h-[16px] w-[16px] flex-none animate-pulse rounded-[4px] bg-surface-2"></div>
                <div class="h-4 w-56 animate-pulse rounded bg-surface-2"></div>
                <div class="ml-auto h-4 w-24 animate-pulse rounded bg-surface-2"></div>
              </div>
            }
          }
        </div>
      } @else if (data()?.tenants?.length === 0) {
        <div class="p-10 text-center text-[13px] text-ink-3">No results. Try using a different query.</div>
      } @else {
        @for (group of data()?.tenants ?? []; track group.tenantId) {
          <div>
            <!-- Group header -->
            <div
              class="flex items-center gap-3 border-b border-hairline bg-sunken px-4 py-3"
              [style.box-shadow]="'inset 4px 0 0 ' + tenantDot(group.tenantId)"
            >
              <button
                type="button"
                (click)="toggleGroupSelection(group)"
                class="flex h-[16px] w-[16px] flex-none items-center justify-center rounded-[4px]"
                [class.bg-action]="groupState(group) !== 'none'"
                [class.border]="groupState(group) === 'none'"
                [class.border-hairline]="groupState(group) === 'none'"
                [class.bg-card]="groupState(group) === 'none'"
              >
                @if (groupState(group) === 'all') {
                  <app-icon name="icon-checkmark" [size]="11" class="text-action-text" />
                } @else if (groupState(group) === 'some') {
                  <span class="h-0.5 w-2 rounded bg-action-text"></span>
                }
              </button>
              <div class="text-[12.5px] font-semibold text-ink-1">{{ group.tenantName }}</div>
              <div class="text-[11.5px] text-ink-3">
                {{ group.totalCount }} devices · {{ group.notAssignedCount }} not assigned
              </div>
              <button
                type="button"
                (click)="toggleGroup(group.tenantId)"
                class="ml-auto flex h-6 w-6 items-center justify-center rounded-[4px] text-ink-2 hover:bg-surface-2"
              >
                <app-icon [name]="isExpanded(group.tenantId) ? 'icon-arrow-collapse' : 'icon-arrow-dropdown'" [size]="18" />
              </button>
            </div>

            @if (isExpanded(group.tenantId)) {
              <div class="h-[320px] border-b border-hairline">
                <app-grid
                  [columns]="deviceColumns"
                  [data]="group.devices"
                  [rowId]="assignRowId"
                  [selectable]="true"
                  [selectedIds]="tenantSelectedIds(group)"
                  (selectedIdsChange)="onTenantSelectionChange(group, $event)"
                  [page]="group.page"
                  [pageSize]="20"
                  [totalElements]="tenantTotalElements(group)"
                  [totalPages]="group.totalPages"
                  density="compact"
                  [showToolbar]="false"
                  (pageChange)="onTenantPageChange(group, $event)"
                  (sortChange)="onSortChange($event)"
                >
                  <ng-template gridCell="clientId" let-row>
                    <span class="truncate font-mono text-[11.5px] text-ink-1">{{ asAssignableDevice(row).clientId }}</span>
                  </ng-template>
                </app-grid>
              </div>
            }
          </div>
        }
      }
    </div>
  </div>

  <!-- Floating selection pill. Only appears once something's selected —
       "Continue to confirm" has nothing to do at zero, and a permanently-
       docked bar felt heavier than this needs to be. Overlays content
       (fixed, not part of the flex column) so it never fights the shell's
       padding the way the old full-width sticky footer had to. -->
  @if (selectedCount() > 0) {
    <div class="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-6">
      <div class="pointer-events-auto flex max-w-[90vw] items-center gap-4 rounded-full border border-hairline bg-card py-2.5 pl-5 pr-2.5 shadow-overlay">
        <div class="flex flex-wrap items-center gap-2 text-[12.5px]">
          <strong class="whitespace-nowrap text-ink-1">{{ selectedCount() }} selected</strong>
          @for (t of selectedByTenant(); track t.tenantId) {
            <app-label-badge [dot]="tenantHueOf(t.tenantId)">{{ t.name }} · {{ t.count }}</app-label-badge>
          }
          <button type="button" (click)="clearSelection()" class="whitespace-nowrap text-link hover:underline">Clear</button>
        </div>
        <div class="h-6 w-px flex-none bg-hairline"></div>
        <div class="flex flex-none gap-2">
          <button type="button" (click)="cancel()" class="rounded-full border border-hairline bg-card px-4 py-2 text-[12.5px] font-semibold text-ink-1 hover:bg-surface-2">
            Cancel
          </button>
          <button
            type="button"
            (click)="continueToConfirm()"
            class="rounded-full bg-action px-4 py-2 text-[12.5px] font-semibold text-action-text hover:brightness-90"
          >
            Continue to confirm
          </button>
        </div>
      </div>
    </div>
  }

  @if (isConfirmOpen()) {
    <app-confirm-assignment-dialog
      [update]="update()"
      [groups]="confirmGroups()"
      (close)="closeConfirm()"
      (assigned)="onBatchAssigned($event)"
    />
  }
</div>
